import { useSelector } from '@tanstack/react-store';
import type { ReactTable, RowData } from '@tanstack/react-table';
import { type RefObject, useEffect } from 'react';

import type { TDataTableFeatures } from '../features/types/features.types.js';

/**
 * Prevents horizontal scrolling while rows are dragged and vertical scrolling while columns are dragged.
 * @param table - The table instance.
 * @param parentRef - The ref of the parent container that is scrollable.
 */
export function DataTableDragScrollLock({
  table,
  parentRef,
}: Readonly<{
  table: ReactTable<TDataTableFeatures, RowData>;
  parentRef: RefObject<HTMLDivElement | null>;
}>): null {
  // Keep drag subscriptions in this null-rendering child component so toggling
  // either drag state does not re-render the virtualization container and every
  // visible row and cell.
  const isRowDragging = useSelector(
    table.atoms.draggingRowInfo,
    (draggingRowInfo) => draggingRowInfo !== null,
  );
  const isColumnDragging = useSelector(
    table.atoms.draggedHeaderColumn,
    (draggedHeaderColumn) => draggedHeaderColumn !== null,
  );
  const rowVirtualizer = table.getRowVirtualizer();
  const columnVirtualizer = table.getColumnVirtualizer();

  useEffect(() => {
    const parent = parentRef.current;
    const currentOffset = rowVirtualizer?.scrollOffset;
    const abort = new AbortController();
    if (isColumnDragging) {
      parent?.addEventListener(
        'scroll',
        () => rowVirtualizer?.scrollToOffset(currentOffset ?? 0),
        { signal: abort.signal },
      );
    }
    return () => abort.abort();
  }, [isColumnDragging, parentRef, rowVirtualizer]);

  useEffect(() => {
    const parent = parentRef.current;
    const currentOffset = columnVirtualizer?.scrollOffset;
    const abort = new AbortController();
    if (isRowDragging) {
      parent?.addEventListener(
        'scroll',
        () => columnVirtualizer?.scrollToOffset(currentOffset ?? 0),
        { signal: abort.signal },
      );
    }
    return () => abort.abort();
  }, [columnVirtualizer, isRowDragging, parentRef]);

  return null;
}
